'use client'

import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { LAYOUT_MAIN_CONTENT_SELECTOR, LAYOUT_RIGHT_DRAWER_SELECTOR } from '../layout.const'
import { concealScrollContainerScrollbar } from '../utils/concealScrollContainerScrollbar'
import { isNodeInsideDrawerOutsideClickExclusion } from '../utils/isNodeInsideDrawerOutsideClickExclusion'
import { positionDrawerWithinViewport } from '../utils/positionDrawerWithinViewport'
import { preventScrollBehindDrawer } from '../utils/preventScrollBehindDrawer'

const DRAWER_ANIMATION_MS = 240
const DRAWER_PHASE = {
  IDLE: 'idle',
  OPENING: 'opening',
  CLOSING: 'closing',
} as const

type DrawerPhase = (typeof DRAWER_PHASE)[keyof typeof DRAWER_PHASE]

type UseRightDrawerParams = {
  pathname: string
}

export function useRightDrawer({ pathname }: UseRightDrawerParams) {
  const [rightDrawerContent, setRightDrawerContent] = useState<ReactNode | null>(null)
  const [drawerPhase, setDrawerPhase] = useState<DrawerPhase>(DRAWER_PHASE.IDLE)
  const rightDrawerRef = useRef<HTMLElement | null>(null)

  const openRightDrawer = useCallback((content: ReactNode) => {
    setRightDrawerContent(content)
    setDrawerPhase(DRAWER_PHASE.OPENING)
  }, [])

  const closeRightDrawer = useCallback(() => {
    setRightDrawerContent((content) => {
      if (!content) {
        return content
      }

      setDrawerPhase((phase) => {
        if (phase === DRAWER_PHASE.CLOSING) {
          return phase
        }

        return DRAWER_PHASE.CLOSING
      })

      return content
    })
  }, [])

  const isScrollLocked = rightDrawerContent !== null || drawerPhase !== DRAWER_PHASE.IDLE

  useLayoutEffect(() => {
    if (!isScrollLocked) {
      return undefined
    }

    const scrollContainer = document.querySelector<HTMLElement>(LAYOUT_MAIN_CONTENT_SELECTOR)
    if (!scrollContainer) {
      return undefined
    }

    const drawerElement = rightDrawerRef.current ?? document.querySelector<HTMLElement>(LAYOUT_RIGHT_DRAWER_SELECTOR)

    const resetDrawerPosition = positionDrawerWithinViewport({ scrollContainer, drawerElement })
    const revealScrollbar = concealScrollContainerScrollbar(scrollContainer)
    const unlockScroll = preventScrollBehindDrawer({
      scrollContainer,
      drawerElement,
    })

    return () => {
      unlockScroll()
      revealScrollbar()
      resetDrawerPosition()
    }
  }, [isScrollLocked, rightDrawerContent])

  useEffect(() => {
    setRightDrawerContent(null)
    setDrawerPhase(DRAWER_PHASE.IDLE)
  }, [pathname])

  useEffect(() => {
    if (drawerPhase !== DRAWER_PHASE.OPENING) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setDrawerPhase(DRAWER_PHASE.IDLE)
    }, DRAWER_ANIMATION_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [drawerPhase])

  useEffect(() => {
    if (drawerPhase !== DRAWER_PHASE.CLOSING) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setRightDrawerContent(null)
      setDrawerPhase(DRAWER_PHASE.IDLE)
    }, DRAWER_ANIMATION_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [drawerPhase])

  useEffect(() => {
    if (!rightDrawerContent) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (isNodeInsideDrawerOutsideClickExclusion(event.target, rightDrawerRef.current)) {
        return
      }

      closeRightDrawer()
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [closeRightDrawer, rightDrawerContent])

  return {
    rightDrawerContent,
    drawerPhase,
    rightDrawerRef,
    openRightDrawer,
    closeRightDrawer,
  }
}
